<script>
  import web from "./web";
  import form from "./form";

  // element.svelte

  /**
   * @type {{
   *   name: string,
   *   props: import("./definition").properties,
   *   data: object,
   *   place?: import("./web").place,
   *   holds?: import("./definition").definition[],
   *   children?: import("svelte").Snippet,
   * }}
   */
  let { name, props, data, place = {}, holds, children } = $props();

  // Web element for the provided component name and props, or nothing for a
  // name web.js does not know or a part whose parts all draw nothing. Derived
  // so it recomputes if the inputs change.
  const ele = $derived(web.map(name, props, data, place, holds));

  // Whether this form's post is on its way, whether it was kept, and whether the last one was not.
  let sending = false;
  let sent = $state(false);
  let failed = $state(false);

  /**
   * The words a control's label shows, or none.
   * @param {HTMLInputElement | null} control
   * @returns {string | undefined}
   */
  const wordsOf = (control) => control?.labels?.[0]?.textContent?.trim();

  /**
   * The name a control's entry is sent under: a choice's question, which is
   * its name, else the words of the label tied to the form's control of its
   * name, or its name.
   * @param {HTMLFormElement} target
   * @param {HTMLInputElement} control
   * @returns {string}
   */
  const labelOf = (target, control) => {
    if (control.type === "radio") {
      return control.name;
    }
    const named = /** @type {HTMLInputElement | null} */ (
      target.elements.namedItem(control.name)
    );
    return wordsOf(named) ?? control.name;
  };

  /**
   * The form's named controls in page order, buttons, choices not ticked and the widget's own left out.
   * @param {HTMLFormElement} target
   * @returns {HTMLInputElement[]}
   */
  const fieldsOf = (target) =>
    [...target.elements]
      .map((control) => /** @type {HTMLInputElement} */ (control))
      .filter(
        (control) =>
          control.name !== "" &&
          !control.disabled &&
          !["submit", "button", "reset"].includes(control.type) &&
          (control.type !== "radio" || control.checked) &&
          !control.closest("altcha-widget"),
      );

  /**
   * A new proof the form's ALTCHA widget solves now, or none when its script is not loaded or it cannot solve.
   * @param {HTMLFormElement} target
   * @returns {Promise<string>}
   */
  const proofOf = async (target) => {
    const widget =
      /** @type {(HTMLElement & { verify?: () => Promise<{ payload?: string } | null> }) | null} */ (
        target.querySelector("altcha-widget")
      );
    if (typeof widget?.verify !== "function") {
      return "";
    }
    const solved = await widget.verify().catch(() => null);
    return solved?.payload ?? "";
  };

  /**
   * What a control sends: Yes or No for a checkbox, a choice's label's
   * words, else what was typed or chosen.
   * @param {HTMLInputElement} control
   * @returns {string}
   */
  const valueOf = (control) => {
    if (control.type === "checkbox") {
      return control.checked ? "Yes" : "No";
    }
    if (control.type === "radio") {
      return wordsOf(control) ?? control.value;
    }
    return control.value;
  };

  /**
   * A link tapped in a panel shown over the page closes the panel, as a jump down the same page leaves it open otherwise.
   * @param {MouseEvent & { currentTarget: HTMLElement }} event
   */
  function tapped(event) {
    web.overOf(event.currentTarget)?.hidePopover();
  }

  /**
   * Posts the form's fields by their labels in page order with a proof the
   * browser solves first, holding Send until the answer comes; kept, the
   * thanks takes the form's place, else the visitor is told to try again.
   * @param {SubmitEvent & { currentTarget: HTMLFormElement }} event
   */
  async function submit(event) {
    event.preventDefault();
    if (sending) {
      return;
    }
    sending = true;
    const target = event.currentTarget;
    const entries = fieldsOf(target).map((control) => ({
      name: labelOf(target, control),
      value: valueOf(control),
    }));
    const send = /** @type {HTMLButtonElement | null} */ (event.submitter);
    if (send) send.disabled = true;
    sent = await form.send(entries, await proofOf(target));
    failed = !sent;
    if (send) send.disabled = false;
    sending = false;
  }
</script>

<svelte:head>
  {#if ele && ele.isMetadata}
    {#if ele.selfClosing}
      <svelte:element this={ele.name} {...ele.attrs} />
    {:else}
      <svelte:element this={ele.name} {...ele.attrs}>
        {#if ele.allowText && ele.text}
          {ele.text}
        {/if}
      </svelte:element>
    {/if}
  {:else if ele && name === "form"}
    <!-- The ALTCHA widget's own bundle, loaded only by a page with a form. -->
    <script type="module" src="/js/altcha.js"></script>
  {/if}
</svelte:head>

{#if ele && !ele.isMetadata}
  {#if sent}
    <p>Thanks, your message was sent.</p>
  {:else if ele.selfClosing}
    <svelte:element this={ele.name} {...ele.attrs} />
  {:else}
    <svelte:element
      this={ele.name}
      {...ele.attrs}
      onsubmit={name === "form" ? submit : undefined}
      onclick={name === "link" ? tapped : undefined}
    >
      <!-- The words and the parts inside touch, so no space is drawn after the words. -->
      {ele.allowText && ele.text
        ? ele.text
        : ""}{@render children?.()}{#if name === "form"}
        <!-- ALTCHA, out of sight: the browser solves its challenge when Send is pressed. -->
        <altcha-widget
          challenge="/_stenzil/form/challenge"
          display="invisible"
          hidden
        ></altcha-widget>
        {#if failed}
          <p role="alert">Your message was not sent. Please try again.</p>
        {/if}
      {/if}
    </svelte:element>
  {/if}
{/if}
